import React, { useEffect, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import MuiDrawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import AppIcons from 'src/utils/image'; // Assuming you have your SVGs here
import { useDispatch, useSelector } from 'react-redux';
import {
  selectBrandSecurity,
  selectLocationSecurity,
  selectReport,
  selectUserProfile,
} from 'src/app/selectors/appDataSelectors';
import { updateSelectedFilter } from 'src/slices/filterSlice';
import { AppConfig } from 'src/config/AppConfig';
import {
  setSelectedMenu,
  setSelectedReportTile,
  setSideMenuWidth,
} from 'src/slices/appDataSlice';
import { useNavigate } from 'react-router-dom';
import { useConfig } from 'src/context/ConfigContext';
import { getPermissions } from 'src/utils/PermissionCheck';
import { selectClientType } from 'src/app/selectors/filterSelectors';

const drawerWidth = 240;
const collapsedDrawerWidth = 60;

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: open ? drawerWidth : collapsedDrawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    width: open ? drawerWidth : collapsedDrawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    backgroundColor: theme.palette.background.default, // Use theme background
  },
}));

interface SidebarProps {
  isOpen?: boolean;
}

const Sidebar: React.FC<SidebarProps> = () => {
  const dispatch = useDispatch();
  const { config } = useConfig();

  const [isOpen, setIsOpen] = useState(true);
  let menuItems = AppConfig.internalUserMenus;

  const [menuItem, setMenuItem] = useState(menuItems[0]);
  const { t } = useTranslation();
  const userProfile = useSelector(selectUserProfile);
  const clientType = useSelector(selectClientType);
  const brandSecurity = useSelector(selectBrandSecurity);
  const locationSecurity = useSelector(selectLocationSecurity);
  const securityData =
    clientType === 'Brand' ? brandSecurity : locationSecurity;

  const { permissions, isbrandAdminRole, superAdmin } = getPermissions(
    userProfile,
    securityData,
    clientType,
  );
  if (userProfile?.dash.superAdmin) {
    menuItems = AppConfig.adminMenus;
  }

  if (clientType == 'NMS') {
    menuItems = AppConfig.nmsMenus;
  }

  const selectedMenu = useSelector(selectReport);
  const navigate = useNavigate();

  const theme = useTheme(); // Access the theme
  // Conditionally use images based on theme mode
  const isDarkMode = theme.palette.mode === 'dark';
  // const iconSet = isDarkMode ? AppIcons.dark : AppIcons.light; // Assuming you have two sets of icons

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
    dispatch(setSideMenuWidth(!isOpen ? collapsedDrawerWidth : drawerWidth));
  };

  useEffect(() => {
    if (clientType == 'NMS') {
      menuItems = AppConfig.nmsMenus;
      if (selectedMenu == 'transactions' || selectedMenu == 'NMS-Alert-Zendesk' ){
      //if (selectedMenu == 'transactions' ){
        dispatch(setSelectedMenu(AppConfig.nmsMenus[0].report));
        navigate('/home/NMS-Alert-Zendesk');
       }
    }
    else {
      dispatch(setSelectedMenu(AppConfig.adminMenus[0].report));
      //navigate('/home/');
      navigate(`/home/${selectedMenu}`);
    }
  }, [clientType]);
  
  function handleSideMenuClick(item: {
    icon: string;
    labelKey: string;
    alt: string;
    report: string;
    tile: string;
  }): void {
    dispatch(setSelectedMenu(item.report));
    setMenuItem(item);
    localStorage.setItem('selectedMenu', item.report);
    dispatch(updateSelectedFilter({ key: 'report', value: item.report }));
    localStorage.setItem('selectedReportTile', item.tile);
    localStorage.setItem('selectedReportTileTitle', item.alt);
    dispatch(setSelectedReportTile(item.tile));
  }

  useEffect(() => {
    // Your API call here

  if (selectedMenu && config) {
   if (selectedMenu) {
     navigate(`/home/${selectedMenu}`);
     menuItems = AppConfig.internalUserMenus;
     if (userProfile?.dash.superAdmin) {
       menuItems = AppConfig.adminMenus;
     }
     if (clientType == 'NMS') {
       menuItems = AppConfig.nmsMenus;
     }
   }
  }
}, [selectedMenu, clientType]);
  return (
    <Drawer variant="permanent" open={isOpen}>
      {/* Top: Toggle Button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: theme.spacing(8),
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          padding: theme.spacing(0, 1),
        }}
      >
        <IconButton onClick={toggleSidebar}>
          {isOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
        {isOpen && (
          <Typography variant="h6" noWrap component="div">
            Menu
          </Typography>
        )}
      </Box>

      <Divider />

      <Box sx={{ flexGrow: 1 }}>
        <ul className="menu-items flex flex-col space-y-4 mt-6">
          {' '}
          {/* Reduced spacing between items */}
          {menuItems.map((item, index) => {
            const isSelected = selectedMenu === item.report;
            const highlightStyle = isSelected
              ? {
                  filter:
                    'invert(35%) sepia(100%) saturate(3200%) hue-rotate(20deg) brightness(110%) contrast(100%)',
                }
              : {};
            if (
              isbrandAdminRole ||
              superAdmin ||
              (permissions && permissions[`dash:brands:${item.report}:view`]) ||
              (clientType == 'NMS' && userProfile.userType == 'internal')
            )
              return (
                <li
                  key={index}
                  className="flex items-center gap-2 pl-4"
                  style={{
                    backgroundColor:
                      selectedMenu === item.report
                        ? theme.palette.action.selected
                        : 'transparent',
                    padding: '10px',
                    borderRight: isSelected
                      ? '4px solid #ff6d0f'
                      : '4px solid transparent',
                    transition:
                      'background-color 0.3s ease, border-right 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      theme.palette.action.selected;
                    e.currentTarget.style.borderRight = '4px solid #ff6d0f';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isSelected
                      ? theme.palette.action.selected
                      : 'transparent';
                    e.currentTarget.style.borderRight = isSelected
                      ? '4px solid #ff6d0f'
                      : '4px solid transparent';
                  }}
                  onClick={() => handleSideMenuClick(item)}
                >
                  <img
                    src={item.icon}
                    alt={item.alt}
                    className="menu-icon h-6 w-6"
                    style={{
                      ...highlightStyle,
                      transition: 'filter 0.3s ease',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.filter =
                        'invert(35%) sepia(100%) saturate(3200%) hue-rotate(20deg) brightness(110%) contrast(100%)')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.filter = isSelected
                        ? e.currentTarget.style.filter
                        : 'none')
                    }
                  />
                  {isOpen && (
                    <span
                      style={{
                        color: theme.palette.text.primary,
                        cursor: 'pointer',
                      }}
                      className="menu-text"
                    >
                      {t(item.labelKey)}
                    </span>
                  )}
                </li>
              );
          })}
        </ul>
      </Box>

      {/* Admin Section */}

      {userProfile && userProfile.dash.superAdmin && (
        <Stack
          direction="row"
          sx={{
            p: theme.spacing(2),
            gap: theme.spacing(1),
            alignItems: 'center',
            borderTop: `1px solid ${theme.palette.divider}`,
            padding: '16px 0px 16px 0px',
          }}
        >
          <li
            key={99}
            className="flex items-center gap-2 pl-4"
            style={{
              backgroundColor:
                selectedMenu === AppConfig.adminTile.report
                  ? theme.palette.action.selected
                  : 'transparent',
              padding: '10px',
              width: isOpen ? drawerWidth : collapsedDrawerWidth,
              borderRight:
                selectedMenu === AppConfig.adminTile.report
                  ? '4px solid #ff6d0f'
                  : '4px solid transparent',
              transition: 'background-color 0.3s ease, border-right 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                theme.palette.action.selected;
              e.currentTarget.style.borderRight = '4px solid #ff6d0f';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                selectedMenu === AppConfig.adminTile.report
                  ? theme.palette.action.selected
                  : 'transparent';
              e.currentTarget.style.borderRight =
                selectedMenu === AppConfig.adminTile.report
                  ? '4px solid #ff6d0f'
                  : '4px solid transparent';
            }}
            onClick={() => handleSideMenuClick(AppConfig.adminTile)}
          >
            <img
              src={AppConfig.adminTile.icon}
              alt={AppConfig.adminTile.alt}
              className="menu-icon h-6 w-6"
              style={{
                filter:
                  selectedMenu === AppConfig.adminTile.report
                    ? 'invert(35%) sepia(100%) saturate(3200%) hue-rotate(20deg) brightness(110%) contrast(100%)'
                    : 'none',
                transition: 'filter 0.3s ease',
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.filter =
                  'invert(35%) sepia(100%) saturate(3200%) hue-rotate(20deg) brightness(110%) contrast(100%)')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.filter =
                  selectedMenu === AppConfig.adminTile.report
                    ? e.currentTarget.style.filter
                    : 'none')
              }
            />
            {isOpen && (
              <span
                style={{ color: theme.palette.text.primary, cursor: 'pointer' }}
                className="menu-text"
              >
                {t(AppConfig.adminTile.labelKey)}
              </span>
            )}
          </li>
        </Stack>
      )}
    </Drawer>
  );
};

export default Sidebar;
